import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import type { SizeBody } from '../data/sizes';
import { Sheet } from './chrome';
import { openGallery } from './galleryNav';
import { Reminder } from './Reminder';
import {
  BODY, BOARDS, BREAK, LICENCE_URL, NAMES_REMINDER, boardBodies, closeSizes, colourNotes, creditsFor, fmtKm, galleryPageFor, listNames, relText, sizeText,
  sizesFocus, spokenLabel, tagOf, type SizeBoard, type SizeGroup,
} from './sizeBoards';
import './sizes.css';

// "How big are they?" (panel 'sizes'): real photographs of the Sun, the planets, the large moons, the dwarf planets and
// some small worlds, cut out and drawn to scale on a few boards, each with Earth or the Moon to compare. Every picture
// on a board is sized from its width in kilometres and the board's width in container units, so the boards keep their
// scale at any screen width or text size. Only the sizes are to scale, never the gaps. The boards and words:
// sizeBoards.ts; the sizes and pictures: src/data/sizes.ts.

const TITLE = 'How big are they?';
/** A picture's address, with its version: a re-cut picture is fetched afresh, not taken from an old cache. */
const src = (p: { file: string; v: string }) => `${import.meta.env.BASE_URL}${p.file}?v=${p.v}`;

/** How far `el` is below the top of the scrolling sheet, less the pinned title (two lines tall with large text). */
function below(scroller: HTMLElement, el: HTMLElement): number {
  const pinned = scroller.querySelector<HTMLElement>('.sheet-title')?.getBoundingClientRect().height ?? 56;
  return el.getBoundingClientRect().top - scroller.getBoundingClientRect().top - pinned - 4;
}
/** A custom property for a style object (the picture's width, the board's span). */
const vars = (v: Record<string, number>) => Object.fromEntries(Object.entries(v).map(([k, n]) => [`--${k}`, String(n)])) as Record<string, string>;

export function SizeCompare() {
  const root = useRef<HTMLDivElement>(null);
  const focus = sizesFocus.value;
  // Opened for one body: its board's heading comes up under the title and the body takes the focus (a screen reader
  // reads it out); a body further down its board is brought into view as well.
  useLayoutEffect(() => {
    const scroller = root.current?.closest<HTMLElement>('.sheet-body');
    // Asked for no body (from the Gallery's home, say), even with the page already open: the top.
    if (!focus.board) return void (focus.at && scroller && (scroller.scrollTop = 0));
    const board = root.current?.querySelector<HTMLElement>(`[data-board="${focus.board}"]`);
    if (!board || !scroller) return;
    scroller.scrollTop += below(scroller, board);
    const hit = focus.body ? board.querySelector<HTMLElement>(`[data-body="${CSS.escape(focus.body)}"]`) : null;
    if (!hit) return;
    const r = hit.getBoundingClientRect(), s = scroller.getBoundingClientRect();
    if (r.bottom > s.bottom - 16) scroller.scrollTop += r.bottom - s.bottom + Math.min(s.height / 3, 160);
    hit.focus({ preventScroll: true });
  }, [focus.at]);
  // Scrolled before the sheet listens for scrolling: its pinned title turns solid now, as it would on a scroll.
  useLayoutEffect(() => {
    const scroller = root.current?.closest<HTMLElement>('.sheet-body');
    if (scroller) scroller.closest('.sheet')?.classList.toggle('scrolled', scroller.scrollTop > 2);
  }, [focus.at]);
  // A later visit starts at the top unless it asks for a body.
  useEffect(() => () => void (sizesFocus.value = { board: null, body: null, at: 0 }), []);
  const jump = (id: string) => {
    const board = root.current?.querySelector<HTMLElement>(`[data-board="${id}"]`);
    const scroller = root.current?.closest<HTMLElement>('.sheet-body');
    if (!board || !scroller) return;
    scroller.scrollTo({ top: scroller.scrollTop + below(scroller, board), behavior: 'smooth' });
    board.querySelector<HTMLElement>('h3')?.focus({ preventScroll: true });
  };
  return (
    <Sheet onClose={closeSizes} size="tall" label={TITLE} class="sizes-sheet">
      <div ref={root} class="sz">
        <h2 class="sheet-title">{TITLE}</h2>
        <p class="sheet-lead">Real photographs, each board drawn to one scale, so the sizes you see are true. Only the sizes: the gaps between the worlds are not to scale.</p>
        <Reminder text={NAMES_REMINDER} />
        <nav class="sz-jump" aria-label="Boards">
          {BOARDS.map((b) => (
            <button key={b.id} class="band-chip" onClick={() => jump(b.id)}>{b.short}</button>
          ))}
        </nav>
        {BOARDS.map((b) => (
          <Board key={b.id} board={b} focus={focus.board === b.id ? focus.body : null} />
        ))}
        <Sources />
        <p class="fine">Each picture is cut out from the original and resized. Tap a world to see more of its pictures in the Gallery.</p>
      </div>
    </Sheet>
  );
}

function Board({ board, focus }: { board: SizeBoard; focus: string | null }) {
  const credits = creditsFor(board);
  const notes = colourNotes(board);
  return (
    <section class="sz-board" data-board={board.id} aria-labelledby={`sz-h-${board.id}`}>
      <h3 class="section-title" id={`sz-h-${board.id}`} tabIndex={-1}>{board.title}</h3>
      <p class="sz-lead">{board.lead}</p>
      <div class="sz-stage">
        {board.arc && <Arc id={board.arc} span={board.groups[0].spanKm} focus={focus === board.arc} />}
        {board.groups.map((g, i) => (
          <Group key={i} board={board} group={g} focus={focus} />
        ))}
      </div>
      <p class="sz-credits">
        <span class="sz-credits-head">Picture credits: </span>
        {credits.map((c, i) => (
          <span key={i}>
            {i > 0 && '; '}
            {listNames(c.names)}, {c.credit}
            {LICENCE_URL[c.licence] && <> (<a href={LICENCE_URL[c.licence]} target="_blank" rel="noopener">{c.licence}</a>)</>}
          </span>
        ))}
        .{notes.length > 0 && ` ${notes.join(' ')}`}
      </p>
    </section>
  );
}

function Group({ board, group, focus }: { board: SizeBoard; group: SizeGroup; focus: string | null }) {
  return (
    <div class="sz-group" style={vars({ span: group.spanKm })}>
      {group.title && <h4 class="sz-group-title">{group.title}</h4>}
      <ul class="sz-row" aria-label={`${group.title ?? board.title}, drawn to scale`}>
        {group.bodies.map((id, i) => (id === BREAK
          ? <li key={`break-${i}`} class="sz-break" role="presentation" aria-hidden="true" />
          : <Item key={id} b={BODY.get(id)!} tags={!!board.tags} focus={focus === id} />))}
      </ul>
      <div class="sz-bar" aria-hidden="true">
        <span class="sz-bar-line" style={vars({ w: group.bar.km })} />
        <span class="sz-bar-label">{group.bar.label}</span>
      </div>
    </div>
  );
}

/** What each world's button says to a screen reader; the words on screen are the same, shown, not read twice. */
const ariaFor = (b: SizeBody) => `${spokenLabel(b)}. ${b.photo ? 'Opens its pictures in the Gallery' : 'Opens the Gallery'}`;

function Pic({ b }: { b: SizeBody }) {
  const p = b.photo;
  if (p) {
    return (
      <img
        class={`sz-pic${p.round ? ' is-round' : ''}${b.kind === 'storm' ? ' is-region' : ''}`}
        src={src(p)}
        width={p.w}
        height={p.h}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        style={vars({ w: p.wKm })}
      />
    );
  }
  // No close-up photograph: a plain grey disc, or for Haumea a plain ellipse of its longest and shortest axes.
  const [w, h] = b.shape === 'ellipse' && b.dims ? [b.dims[0], b.dims[b.dims.length - 1]] : [b.km, b.km];
  return <span class="sz-blank" style={vars({ w, h })} />;
}

function Item({ b, tags, focus }: { b: SizeBody; tags: boolean; focus: boolean }) {
  const tag = tags ? tagOf(b) : null;
  return (
    <li class={`sz-item${focus ? ' is-focus' : ''}${b.photo ? '' : ' is-blank'}`}>
      <button class="sz-hit" data-body={b.id} aria-label={ariaFor(b)} onClick={() => openGallery(galleryPageFor(b.id), undefined, 'sizes', b.id)}>
        <span class="sz-pic-box">
          <Pic b={b} />
        </span>
        <span class="sz-label" aria-hidden="true">
          <span class="sz-name">{b.name}</span>
          <span class="sz-size">{sizeText(b)}</span>
          <span class="sz-rel">{relText(b)}</span>
          {tag && <span class="sz-tag">{tag}</span>}
          {b.blank && <span class="sz-tag sz-none">{b.blank}</span>}
        </span>
      </button>
    </li>
  );
}

/** The Sun as a great arc along the top of its board: only its lower edge fits, at the same scale as the planets. */
function Arc({ id, span, focus }: { id: string; span: number; focus: boolean }) {
  const b = BODY.get(id)!;
  const p = b.photo!;
  return (
    <div class={`sz-arc${focus ? ' is-focus' : ''}`} style={vars({ span, w: p.wKm })}>
      <button class="sz-hit sz-arc-hit" data-body={id} aria-label={ariaFor(b)} onClick={() => openGallery(galleryPageFor(id), undefined, 'sizes', id)}>
        <span class="sz-arc-cap">
          <img class="sz-arc-pic" src={src(p)} width={p.w} height={p.h} alt="" loading="lazy" decoding="async" draggable={false} />
        </span>
        <span class="sz-label sz-arc-label" aria-hidden="true">
          <span class="sz-name">{b.name}</span>
          <span class="sz-size">{sizeText(b)}</span>
          <span class="sz-rel">{relText(b)}</span>
        </span>
      </button>
    </div>
  );
}

/** Each world's size, how it is defined and where it comes from, once, in page order. */
function Sources() {
  const ids = [...new Set(BOARDS.flatMap(boardBodies))];
  return (
    <details class="sources sz-sources">
      <summary>Where the sizes and pictures come from</summary>
      <ol>
        {ids.map((id) => {
          const b = BODY.get(id)!;
          const p = b.photo;
          return (
            <li key={id}>
              <strong>{b.name}</strong>: {fmtKm(b.km)}, {b.basis}. <a href={b.source.url} target="_blank" rel="noopener">{b.source.label}</a>.
              {p ? <> Picture: <a href={p.page} target="_blank" rel="noopener">{p.credit}</a>.</> : ''}
              {b.note && <> {b.note}</>}
            </li>
          );
        })}
      </ol>
    </details>
  );
}
